import React, { useCallback, useEffect, useState } from "react";
import {
  DataTable,
  TableAction,
  TableColumn,
} from "../../../components/DataTable";
import { gestionCurricularOptions } from "../../../config/entityColors";
import {
  BulkUploadData,
  gestionCurricularService,
} from "../../../services/admin/gestion-curricular/gestion-curricular.service";
import { AdminLayout } from "../components";
import { BulkUpload } from "../components/BulkUpload";
import { EntitySelector } from "../components/EntitySelector";

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
}

type EntityType = "tema" | "anioEscolar" | "grupo" | "unidad";

interface FormData {
  nombre: string;
  orden: number;
}

export const GestionCurricularPage: React.FC = () => {
  const [selectedEntity, setSelectedEntity] = useState<EntityType>("tema");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nombre: "",
    orden: 1,
  });

  // Estados para cada entidad
  const [temas, setTemas] = useState<any[]>([]);
  const [aniosEscolares, setAniosEscolares] = useState<any[]>([]);
  const [grupos, setGrupos] = useState<any[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Estado para notificaciones
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
  });

  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);

  // Estados para carga masiva
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  // Funciones para manejar notificaciones
  const showNotification = useCallback(
    (
      type: "success" | "error" | "info" | "warning",
      title: string,
      message: string
    ) => {
      setNotification({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    []
  );

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }));
  };

  // Función para mostrar confirmación de eliminación
  const confirmDelete = (id: number) => {
    setItemToDelete(id);
    setShowDeleteConfirm(true);
  };

  // Función para cancelar eliminación
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  // Funciones para carga masiva
  const handleDownloadTemplate = async () => {
    try {
      const blob = await gestionCurricularService.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_contenido_curricular.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification("success", "Éxito", "Plantilla descargada exitosamente");
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.message || "Error al descargar la plantilla"
      );
    }
  };

  const handleBulkUpload = async (data: BulkUploadData[]) => {
    setBulkUploadLoading(true);
    try {
      const result = await gestionCurricularService.bulkUpload(data);

      // Mostrar resumen simplificado
      const totalCreados =
        result.creados.aniosEscolares +
        result.creados.unidades +
        result.creados.temas +
        result.creados.contenidos;
      const totalExistentes =
        result.existentes.aniosEscolares +
        result.existentes.unidades +
        result.existentes.temas;

      let mensaje = `Carga masiva completada exitosamente.\n\n`;
      mensaje += `✅ Creados: ${totalCreados} elementos\n`;
      mensaje += `ℹ️ Existentes: ${totalExistentes} elementos\n`;

      if (result.errores.length > 0) {
        mensaje += `⚠️ ${result.errores.length} filas con errores fueron omitidas`;
      }

      showNotification("success", "Carga Masiva Completada", mensaje);

      // Recargar datos
      await cargarDatos();
      setShowBulkUpload(false);
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.message || "Error en la carga masiva"
      );
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Cargar datos según la entidad seleccionada
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const baseURL = process.env.REACT_APP_API_URL;
      if (!baseURL) {
        throw new Error("URL de API no configurada");
      }

      let endpoint = "";
      switch (selectedEntity) {
        case "tema":
          endpoint = "/contenido-curricular/temas-contenido";
          break;
        case "anioEscolar":
          endpoint = "/contenido-curricular/anios-escolares";
          break;
        case "grupo":
          endpoint = "/contenido-curricular/grupos-contenido";
          break;
        case "unidad":
          endpoint = "/contenido-curricular/unidades-contenido";
          break;
      }

      const response = await fetch(`${baseURL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const items = data.data || [];

      switch (selectedEntity) {
        case "tema":
          setTemas(items);
          break;
        case "anioEscolar":
          setAniosEscolares(items);
          break;
        case "grupo":
          setGrupos(items);
          break;
        case "unidad":
          setUnidades(items);
          break;
      }
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.message || "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedEntity, showNotification]);

  // Cargar datos al cambiar de entidad
  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const resetForm = () => {
    setFormData({
      nombre: "",
      orden: 1,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const baseURL = process.env.REACT_APP_API_URL;
      if (!baseURL) {
        throw new Error("URL de API no configurada");
      }

      let endpoint = "";
      switch (selectedEntity) {
        case "tema":
          endpoint = isEditing
            ? `/contenido-curricular/temas-contenido/${editingId}`
            : "/contenido-curricular/temas-contenido";
          break;
        case "anioEscolar":
          endpoint = isEditing
            ? `/contenido-curricular/anios-escolares/${editingId}`
            : "/contenido-curricular/anios-escolares";
          break;
        case "grupo":
          endpoint = isEditing
            ? `/contenido-curricular/grupos-contenido/${editingId}`
            : "/contenido-curricular/grupos-contenido";
          break;
        case "unidad":
          endpoint = isEditing
            ? `/contenido-curricular/unidades-contenido/${editingId}`
            : "/contenido-curricular/unidades-contenido";
          break;
      }

      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(`${baseURL}${endpoint}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      await cargarDatos();
      closeModal();
      showNotification(
        "success",
        "Éxito",
        isEditing
          ? `${getEntityName()} actualizado exitosamente`
          : `${getEntityName()} creado exitosamente`
      );
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.message || "Error al procesar la solicitud"
      );
    }
  };

  const handleEdit = (item: any) => {
    setFormData({
      nombre: item.nombre,
      orden: item.orden,
    });
    setEditingId(
      item.idTemaContenido ||
        item.idAnioEscolar ||
        item.idGrupoContenido ||
        item.idUnidadContenido
    );
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        throw new Error("No hay token de autenticación");
      }

      const baseURL = process.env.REACT_APP_API_URL;
      if (!baseURL) {
        throw new Error("URL de API no configurada");
      }

      let endpoint = "";
      switch (selectedEntity) {
        case "tema":
          endpoint = `/contenido-curricular/temas-contenido/${id}`;
          break;
        case "anioEscolar":
          endpoint = `/contenido-curricular/anios-escolares/${id}`;
          break;
        case "grupo":
          endpoint = `/contenido-curricular/grupos-contenido/${id}`;
          break;
        case "unidad":
          endpoint = `/contenido-curricular/unidades-contenido/${id}`;
          break;
      }

      const response = await fetch(`${baseURL}${endpoint}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      await cargarDatos();
      showNotification(
        "success",
        "Éxito",
        `${getEntityName()} eliminado exitosamente`
      );
      cancelDelete();
    } catch (error: any) {
      showNotification("error", "Error", error.message || "Error al eliminar");
    }
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const getEntityTitle = () => {
    switch (selectedEntity) {
      case "tema":
        return "Temas";
      case "anioEscolar":
        return "Años Escolares";
      case "grupo":
        return "Grupos";
      case "unidad":
        return "Unidades";
      default:
        return "";
    }
  };

  const getCurrentData = () => {
    switch (selectedEntity) {
      case "tema":
        return temas || [];
      case "anioEscolar":
        return aniosEscolares || [];
      case "grupo":
        return grupos || [];
      case "unidad":
        return unidades || [];
      default:
        return [];
    }
  };

  const getEntityName = () => {
    switch (selectedEntity) {
      case "tema":
        return "Tema";
      case "anioEscolar":
        return "Año Escolar";
      case "grupo":
        return "Grupo";
      case "unidad":
        return "Unidad";
      default:
        return "";
    }
  };

  // Configuración de columnas para la tabla
  const columns: TableColumn[] = [
    { key: "nombre", label: "Nombre" },
    { key: "orden", label: "Orden" },
    { key: "actions", label: "Acciones", type: "actions" },
  ];

  // Configuración de acciones para la tabla
  const actions: TableAction[] = [
    {
      label: "Editar",
      onClick: handleEdit,
      variant: "edit",
    },
    {
      label: "Eliminar",
      onClick: (item) =>
        confirmDelete(
          item.idTemaContenido ||
            item.idAnioEscolar ||
            item.idGrupoContenido ||
            item.idUnidadContenido
        ),
      variant: "delete",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-blue-600">
              Gestión Curricular
            </h1>
            <p className="text-gray-600">
              Gestiona temas, años escolares, grupos y unidades
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowBulkUpload(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md flex items-center space-x-2"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span>Carga Masiva</span>
            </button>
            <button
              onClick={openCreateModal}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
            >
              Crear Nuevo {getEntityName()}
            </button>
          </div>
        </div>

        {/* Modal de Carga Masiva */}
        {showBulkUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-blue-100">
                <h2 className="text-xl font-semibold text-gray-900">
                  Carga Masiva de Contenido Curricular
                </h2>
                <button
                  onClick={() => setShowBulkUpload(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <BulkUpload
                  title="Carga Masiva de Contenido Curricular"
                  description="Sube un archivo Excel con años, unidades y temas para crear el contenido curricular de forma masiva"
                  onUpload={handleBulkUpload}
                  onDownloadTemplate={handleDownloadTemplate}
                  loading={bulkUploadLoading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Selector de entidad */}
        <EntitySelector
          selectedEntity={selectedEntity}
          onEntityChange={(entityId) =>
            setSelectedEntity(entityId as EntityType)
          }
          options={gestionCurricularOptions}
        />

        {/* Tabla de registros */}
        <DataTable
          title={getEntityTitle()}
          data={getCurrentData()}
          columns={columns}
          actions={actions}
          loading={loading}
          emptyMessage={`No hay ${getEntityTitle().toLowerCase()} registrados.`}
          pagination={{ enabled: true, itemsPerPage: 10 }}
        />

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 border border-blue-100">
              <div className="flex justify-between items-center p-6 border-b border-blue-100">
                <h2 className="text-xl font-semibold text-gray-900">
                  {isEditing
                    ? `Editar ${getEntityName()}`
                    : `Crear ${getEntityName()}`}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold transition-colors"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) =>
                      setFormData({ ...formData, nombre: e.target.value })
                    }
                    required
                    className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder={`Nombre del ${getEntityName().toLowerCase()}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Orden *
                  </label>
                  <input
                    type="number"
                    value={formData.orden}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        orden: parseInt(e.target.value),
                      })
                    }
                    required
                    min="1"
                    className="w-full px-4 py-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Orden de aparición"
                  />
                </div>
                <div className="flex space-x-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
                  >
                    {isEditing ? "Actualizar" : "Crear"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notificación */}
        {notification.isOpen && (
          <div className="fixed top-4 right-4 z-50">
            <div
              className={`rounded-xl shadow-lg p-4 max-w-sm border ${
                notification.type === "success"
                  ? "bg-green-500 text-white border-green-400"
                  : notification.type === "error"
                  ? "bg-red-500 text-white border-red-400"
                  : notification.type === "warning"
                  ? "bg-yellow-500 text-white border-yellow-400"
                  : "bg-blue-500 text-white border-blue-400"
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{notification.title}</div>
                  <div className="text-sm opacity-90">
                    {notification.message}
                  </div>
                </div>
                <button
                  onClick={closeNotification}
                  className="ml-4 text-white hover:text-gray-200 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmación de eliminación */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 border border-blue-100">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Confirmar eliminación
                    </h3>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    ¿Estás seguro de que quieres eliminar este{" "}
                    {getEntityName().toLowerCase()}? Esta acción no se puede
                    deshacer.
                  </p>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleDelete(itemToDelete!)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
                  >
                    Eliminar
                  </button>
                  <button
                    onClick={cancelDelete}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
